import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { Grid, Box, Tabs, makeStyles, Collapse, Tab } from '@material-ui/core';

import useMobile from 'hooks/useMobile.hook';
import useHistoryQuery from 'hooks/useHistoryQuery.hook';
import { getArray } from 'utils/helpers';

const categories = [
  {
    starts: 'A',
    ends: 'H',
  },
  {
    starts: 'I',
    ends: 'Z',
  },
];

const tabs = categories.map((cat) => ({
  value: [cat.starts, cat.ends].join(''),
  label: [cat.starts, cat.ends].join('-').toUpperCase(),
  category: cat,
}));

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    transition: theme.transitions.create(),
    padding: theme.spacing(2, 3),
  },
  active: {
    backgroundColor: '#F8F8FA',
  },
  tabs: {
    minHeight: '0px',
  },
  indicator: {
    height: '4px',
    borderRadius: '2px',
  },
  tab: {
    padding: theme.spacing(1, 1.5),
    minHeight: '0px',
    minWidth: '0px',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 800,
  },
}));

const NavContentMobile = (props) => {
  const { currentCategory, categoriesList, setActive, styles } = props;
  const classes = useStyles();
  const query = useHistoryQuery();

  const [value, setValue] = useState(tabs[0].value);

  useEffect(() => {
    if (!currentCategory) {
      setValue(tabs[0].value);
    }
  }, [currentCategory]);

  const content = useMemo(() => {
    const tab = tabs.find((t) => t.value === value);

    if (!tab) {
      return [];
    }
    const targetCategory = getArray(categoriesList).find(
      (cat) => cat.id === currentCategory,
    );

    if (!targetCategory) {
      return [];
    }
    const subcategories = getArray(targetCategory?.subcategories).filter(
      (sub) => {
        return (
          sub.title[0].toUpperCase() >= tab.category.starts &&
          sub.title[0].toUpperCase() <= tab.category.ends
        );
      },
    );
    return subcategories.sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  }, [categoriesList, currentCategory, value]);

  return (
    <div
      className={cx(classes.root, currentCategory !== null && classes.active)}
    >
      <Collapse in={currentCategory !== null}>
        <div>
          <Tabs
            value={value}
            onChange={(e, v) => setValue(v)}
            variant="scrollable"
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
            classes={{
              indicator: classes.indicator,
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                className={classes.tab}
              />
            ))}
          </Tabs>
        </div>
      </Collapse>

      {currentCategory && (
        <Box mt={3}>
          <ul className={styles.listContainer}>
            {content.map((sub) => {
              const link = query.setValueLink(
                'categories',
                [sub.search_engine_id],
                {
                  pathname: '/products',
                  replace: true,
                },
              );
              return !link ? null : (
                <li
                  key={sub.id}
                  onClick={() => setTimeout(() => setActive(false), 0)}
                >
                  <Link to={link}>{sub.title}</Link>
                </li>
              );
            })}
          </ul>

          {content.length === 0 && (
            <Box component="span" color="silver">
              No categories found
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

const NavContentDesktop = (props) => {
  const { categoriesList, styles, currentCategory, setActive } = props;
  const query = useHistoryQuery();

  return (
    <Grid container spacing={3} className={styles.categiriesList}>
      <Grid item xs={4}>
        <h2>A–H</h2>
        <ul className={styles.listContainer}>
          {categoriesList
            ?.filter((cat) => cat.id === currentCategory)[0]
            ?.subcategories.filter(
              (l) => l.title[0] >= 'A' && l.title[0] <= 'H',
            )
            .sort((a, b) => {
              return a.title.localeCompare(b.title);
            })
            .map((sub) => {
              const link = query.setValueLink(
                'categories',
                [sub.search_engine_id],
                {
                  pathname: '/products',
                  replace: true,
                },
              );
              return !link ? null : (
                <li
                  key={sub.id}
                  onClick={() => {
                    setTimeout(() => {
                      setActive(false);
                    }, 0);
                  }}
                >
                  <Link to={link}>{sub.title}</Link>
                </li>
              );
            })}
        </ul>
      </Grid>
      <Grid item xs={4}>
        <h2>I–Z</h2>
        <ul className={styles.listContainer}>
          {categoriesList
            ?.filter((cat) => cat.id === currentCategory)[0]
            ?.subcategories.filter(
              (l) => l.title[0] >= 'I' && l.title[0] <= 'Z',
            )
            .sort((a, b) => {
              return a.title.localeCompare(b.title);
            })
            .map((sub) => {
              const link = query.setValueLink(
                'categories',
                [sub.search_engine_id],
                {
                  pathname: '/products',
                  replace: true,
                },
              );
              return !link ? null : (
                <li
                  key={sub.id}
                  onClick={() => {
                    setTimeout(() => {
                      setActive(false);
                    }, 0);
                  }}
                >
                  <Link to={link}>{sub.title}</Link>
                </li>
              );
            })}
        </ul>
      </Grid>
    </Grid>
  );
};

const NavigationContent = (props) => {
  const isMobile = useMobile();
  const Component = isMobile ? NavContentMobile : NavContentDesktop;

  return <Component {...props} />;
};

export default NavigationContent;
